import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Color from 'color';
import LeafSvg from 'images/maxmortgage/leaf.svg';
import { currencyFormatter, percentageFormatter } from '@dnhb/formatters';
import { useState } from 'react';
import { useDispatch, useSelector } from 'shared/hooks';
import { Collapsable, Currency, Heading, Info, Loader } from 'shared/components';
import { FlexContainer, ResultCard } from 'shared/containers';
import ConversieButtons from 'shared/conversie/ConversieButtons';
import { getParamsFromState } from 'shared/helpers';
import useTimeout from 'shared/hooks/useTimeout';
import { Checkbox } from 'shared/inputs';
import { dispatchWithRequest, SYNC, sync, BROKEN } from 'shared/wizard/actions';
import { sortFrtResults } from '../helpers';
import FrtRadioButton from './FrtRadioButton';
import { whitelistUrlParams, blacklistOutputUrlParams } from './../config';

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;

    > div {
        margin-bottom: 16px;

        &.extra-options {
            > div > div {
                margin-bottom: 16px;

                &:last-of-type {
                    margin: 0;
                }
            }
        }

        &:last-of-type {
            margin-bottom: 0;
            @media ${({theme}) => theme.tablet} {
                order: 1;
                margin-bottom: 16px;
            }
        }
    }
`;

const ResultContainer = styled(ResultCard)`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;

    color: ${({theme}) => theme.primaryColor};
    font-size: 12px;
    line-height: 12px;
    font-weight: 600;

    .title {
        font-size: 14px;
        font-weight: ${({nhg}) => (nhg ? '800' : null)};
        letter-spacing: -0.47px;
        color: ${({nhg, theme}) => (!nhg ? theme.primaryFontColor : null)};
        margin-bottom: 12px;
    }

    .provider {
        height: 24px;
        margin-bottom: 12px;

        img {
            display: block;
            height: 100%;
        }
    }

    .details {
        color: ${({theme}) => theme.primaryFontColor};
    }

    strong {
        font-weight: 800;
    }

    .gross-payment {
        margin-right: 16px;
    }

    @media ${({theme}) => theme.mobile} {
        .payments {
            flex-direction: column;

            .gross-payment {
                margin: 0 0 8px;
            }
        }
    }
`;

const LoaderContainer = styled.div`
    display: flex;
    border-radius: inherit;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    background-color: ${({theme}) =>
            Color(theme.cardColor)
                    .alpha(0.7)
                    .rgb()
                    .string()};
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
`;

const OutputFinal = () => {
  const dispatch = useDispatch();
  const {app, input, output, settings, syncing} = useSelector(state => state);
  const [frtButtonValue, setFrtButtonValue] = useState(input.fixed_rate_term);
  const setReduxFrtData = useTimeout(() => {
    dispatch({type: SYNC, syncing: false});
    sync(dispatch)('fixed_rate_term', Number(frtButtonValue));
  }, 500);

  if (!output.frt_data) {
    // The only way to get no output here is if the state resetted without
    // refreshing. By showing a broken page, we force the user to refresh.
    dispatch({type: BROKEN});
    return null;
  }

  const {energySavingMeasuresSwitch, nonDeductableAmountSwitch, newRegulationsSwitch} = settings;
  const nextYear = String(new Date().getFullYear() + 1);
  const resultsByFrt = output.frt_data?.find(object => object.frt_years === input.fixed_rate_term) ?? [];

  const onChangeHandler = (prop, value) => {
    dispatch({type: SYNC, syncing: true});
    dispatch(
      dispatchWithRequest(prop, value, '/maxmortgage/calculate/extra', '', () =>
        dispatch({type: SYNC, syncing: false})
      )
    );
  };
  const showExtraOptions = newRegulationsSwitch || energySavingMeasuresSwitch || nonDeductableAmountSwitch;
  const chosenMaxMortgage = output.frt_data?.find(item => item.frt_years == input.fixed_rate_term);

  const paramsFromState = getParamsFromState({
    ...input,
    ...Object.keys(output).reduce((mem, key) => {
      if (!blacklistOutputUrlParams.includes(key))
        mem[key] = output[key];
      return mem;
    }, {}),
    frt: input.fixed_rate_term,
    max_mortgage: chosenMaxMortgage ? Math.max(
      chosenMaxMortgage?.nhg?.maximum_mortgage ?? 0, chosenMaxMortgage?.without_nhg?.maximum_mortgage ?? 0) : 0,
    nhg: Number(
      Boolean(chosenMaxMortgage?.nhg?.maximum_mortgage ?? 0 > chosenMaxMortgage?.without_nhg?.maximum_mortgage ?? 0))
  }, whitelistUrlParams);

  return (
    <Wrapper>
      <Heading>Je maximale hypotheek</Heading>

      <Results
        hideProvidersInResult={settings.hideProvidersInResult}
        energySavingMeasures={input.energy_saving_measures}
        energyLabelAddition={output.energy_saving_extra_max_mortgage_amount}
        newRegulations={input.new_regulations}
        {...resultsByFrt}
        syncing={syncing}
      />

      <Collapsable
        title="Toon andere rentevaste periode"
        css={css`
            > div {
                padding: 16px 24px;
            }
        `}
      >
        {output.frt_data?.map(frt => (
          <FrtRadioButton
            key={frt.frt_years}
            fixed_rate_term={frtButtonValue}
            frt={frt}
            name="frt-options"
            onChange={e => {
              dispatch({type: SYNC, syncing: true});
              const value = Number(e.target.value);
              setFrtButtonValue(value);
              setReduxFrtData(value);
            }}
          />
        ))}
      </Collapsable>
      {showExtraOptions && (
        <Collapsable title="Waar wil je rekening mee houden?" className="extra-options">
          <div
            css={css`
                > div:not(:last-of-type) {
                    margin-bottom: 16px;
                }
            `}
          >
            {energySavingMeasuresSwitch && (
              <Checkbox
                name="energy_saving_measures"
                value={input.energy_saving_measures}
                onChange={e => onChangeHandler('energy_saving_measures', e.target.value)}
              >
                Ik wil extra lenen voor energiebesparende maatregelen
                <Info title="Energie&shy;besparende maatregelen">
                  <p>
                    Ga je geld lenen voor het aanbrengen van energiebesparende voorzieningen? Dan kun je op basis van je
                    inkomen tot en met {currencyFormatter(output.energy_saving_extra_max_mortgage_amount)} extra bijlenen. Bij energiebesparende voorzieningen kun je denken aan
                    bijvoorbeeld gevelisolatie en zonnecellen. Vink je deze optie aan, dan rekenen we met dit bedrag.
                  </p>
                </Info>
              </Checkbox>
            )}
            {newRegulationsSwitch && (
              <Checkbox
                name="new_regulations"
                value={input.new_regulations}
                onChange={e => {
                  sync(dispatch)('new_regulations', e.target.value)
                  // also change energy label to next year regulations
                  if (e.target.value && ['ENERGY_NEUTRAL', 'ZERO-ON-THE-METER'].includes(input.energy_label)) {
                    onChangeHandler('energy_label', 'A++++_WITH_ENERGY_PERFORMANCE_GUARANTEE');
                  } else if (input.energy_label === 'A++++_WITH_ENERGY_PERFORMANCE_GUARANTEE') {
                    onChangeHandler('energy_label', 'ZERO-ON-THE-METER');
                  } else if (input.energy_label === 'A+++++') {
                    onChangeHandler('energy_label', 'A++++');
                  } else {
                    // trigger submit
                    onChangeHandler('new_regulations', e.target.value);
                  }
                }}
                label={`Toon mijn maximale hypotheek als ik deze afsluit in ${nextYear}`}
              />
            )}
            {nonDeductableAmountSwitch && (
              <Checkbox
                name="mortgage_interest_deduction"
                value={!input.mortgage_interest_deduction}
                onChange={e => onChangeHandler('mortgage_interest_deduction', !e.target.value)}
              >
                Ik heb geen recht op hypotheekrenteaftrek
                <Info title="Hypotheekrenteaftrek">
                  Je mag de hypotheekrente aftrekken bij je belastingaangifte, wanneer je met deze lening een woning
                  aankoopt, verbetert of onderhoudt. Dit geldt alleen voor een woning die je gebruikt als hoofdverblijf
                  en er geldt een maximum termijn van 30 jaar. Sinds 2013 mag je alleen rente aftrekken voor een
                  ‘eigenwoningschuld’ die je volledig en binnen 30 jaar minimaal annuïtair aflost. Je komt o.a. niet in
                  aanmerking voor aftrek van de rente bij een hypotheek op een tweede woning, voor consumptieve
                  bestedingen, of als je niet (jaarlijks) aflost.
                </Info>
              </Checkbox>
            )}
          </div>
        </Collapsable>
      )}
      <ConversieButtons
        app={app}
        settings={settings}
        urlLeadFormParams={paramsFromState}
      />
    </Wrapper>
  );
};

const Results = ({nhg, without_nhg, ...props}) => {
  const results = [nhg, without_nhg]
    .filter(item => item) // Remove NULL results
    .sort(sortFrtResults);

  return results.map(result => <Result key={result.nhg ? 'nhg' : 'nonhg'} {...result} {...props} />);
};

const LogoContainer = styled.div(
  ({theme}) => css`
      display: inline-block;
      margin-left: 8px;

      svg {
          fill: ${theme.highlightColor};
          display: block;
          height: 16px;
          width: 16px;
      }
  `
);

const Result = ({
  maximum_mortgage,
  fixed_rate_term,
  mortgage_provider_id,
  mortgage_provider_name,
  interest_rate,
  gross_payment,
  net_payment,
  nhg,
  hideProvidersInResult,
  newRegulations,
  energySavingMeasures,
  energyLabelAddition,
  syncing,
}) => {
  const nextYear = String(new Date().getFullYear() + 1);
  const energySavingLogo = (
    <LogoContainer>
      <LeafSvg />
    </LogoContainer>
  );
  return (
    <ResultContainer nhg={nhg}>
      {syncing && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      <span className="title">
        {nhg ? 'Met' : 'Zonder'} NHG {newRegulations && `in ${nextYear}`}
      </span>
      {!hideProvidersInResult && (
        <div className="provider">
          <img
            src={`https://h.hstatic.nl/bank/${mortgage_provider_id}/logo-groot`}
            alt={mortgage_provider_name}
          />
        </div>
      )}
      <div css={{marginBottom: '15px'}}>
        <Currency value={maximum_mortgage} fontSize="24px" fontWeight="800" />
        {energySavingMeasures && energySavingLogo}
      </div>
      <span className="details">
        {`O.b.v. ${percentageFormatter(interest_rate)} en ${fixed_rate_term} jaar rentevast`}
      </span>
      <FlexContainer className="payments">
        <span className="gross-payment">
          Netto maandlast <strong>{currencyFormatter(net_payment)}</strong>
        </span>
        <span>
          Bruto maandlast <strong>{currencyFormatter(gross_payment)}</strong>
        </span>
      </FlexContainer>
      {energyLabelAddition && !energySavingMeasures
        ? (
          <FlexContainer>
            <span className="details">Je kunt nog tot {currencyFormatter(energyLabelAddition)} extra lenen voor energiebesparende maatregelen</span>
          </FlexContainer>
        ) : null}
    </ResultContainer>
  );
};

export default OutputFinal;
